import { Grid } from '@mui/material';
import { RoutalLoader, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { dateFormatShortTime, parseSecondsToHM } from '@nx-smartmonkey/shared/helpers';
import { capacitorService } from '../../../../../../application/CapacitorService';
import { DraggableContainer } from '../../../../../../components/DraggableContainer';
import { Modal } from '../../../../../../components/Modal';
import { ModalSelect } from '../../../../../../components/ModalSelect';
import { EventTrackTypes } from '../../../../../../constants/EventTracks';
import {
  ROUTE_END_LOCATION,
  ROUTE_START_LOCATION,
  ROUTE_STATUS,
  ROUTE_STOPS_BLACKLIST,
} from '../../../../../../constants/Routes';
import * as routing from '../../../../../../constants/Routing';
import { STOP_STATUS } from '../../../../../../constants/Stops';
import { Route } from '../../../../../../domain/route/Route';
import { Stop } from '../../../../../../domain/stop/Stop';
import { intl } from '../../../../../../helpers/IntGlobalProvider';
import { useCaptureEvents } from '../../../../../../hooks/useCaptureEvents';
import { MapsApplicationTypes } from '../../../../../../interfaces/MapsApplication';
import { Text } from '../../../../../../react-components/Text';
import { usePermissions } from '../../../../hooks/usePermissions';
import { useRetrieveDriverCredentials } from '../../../../hooks/useRetrieveDriverCredentials';
import { useRetrieveVehicle } from '../../../../hooks/useRetrieveVehicle';
import { RouteActionType } from '../../../context/actions';
import { useRouteContext } from '../../../context/context';
import { useDeleteStop } from '../../../hooks/useDeleteStop';
import { useStartStop } from '../../../hooks/useStartStop';
import { useUpdateRouteStopsOrder } from '../../../hooks/useUpdateRouteStopsOrder';
import { useUpdateStop } from '../../../hooks/useUpdateStop';
import { EditStopSlidingMenu } from '../../EditStopSlidingMenu';
import { RouteFooter } from '../../route-stops-footers/RouteFooter';
import { RouteFooterContainer } from '../../route-stops-footers/RouteFooterContainer';
import { RouteDragIcon } from '../../RouteDragIcon';
import { StopPillInfo } from '../../StopPillInfo';
import { useFetchMapsApplication } from '../hooks/useFetchMapsApplication';
import { ActionButtons, openNavigationLink } from './action-buttons/ActionButtons';
import { StopDetails } from './StopDetails';
import { StopDetailsFooter } from './StopDetailsFooter';

const AMOUNT_STOPS = 7;

interface StopDetailsContainerProps {
  selectedRoute?: Route;
  footer?: boolean;
  onUpdate?: (stopId: string, updateType: `complete` | `cancel` | `discard`) => void;
  endRoute?: () => void;
  discardLoading?: boolean;
  reportLoading?: boolean;
  allStopsCompleted?: boolean;
}

export const StopDetailsContainer = ({
  selectedRoute,
  footer = true,
  onUpdate,
  discardLoading,
  reportLoading,
  endRoute,
  allStopsCompleted,
}: StopDetailsContainerProps) => {
  const { stopId } = useParams();
  const navigate = useNavigate();
  const { capture } = useCaptureEvents();
  const { data: vehicleData } = useRetrieveVehicle();
  const { data: credentials } = useRetrieveDriverCredentials();
  const { canReorderStops } = usePermissions();
  const [showMapsModal, setShowMapsModal] = useState(false);
  const [preferredMapModal, setPreferredMapModal] = useState<MapsApplicationTypes | undefined>(undefined);
  const [stopCountingModal, setStopCountingModal] = useState(false);
  const [stopPickupModal, setStopPickupModal] = useState<Stop | undefined>(undefined);
  const [stopDiscardWithDelivery, setStopDiscardWithDelivery] = useState<Stop | undefined>(undefined);
  const [skipModalStop, setSkipModalStop] = useState<string | undefined>(undefined);
  const { routeState, routeDispatch } = useRouteContext();
  const { startedStopId, setReportDate, clearReportDate } = useStartStop();
  const { run: deleteStop, isLoading: deletingStop } = useDeleteStop({ route: selectedRoute! });
  const { run: updateStop, isLoading: updatingStop, success: stopUpdated } = useUpdateStop({ route: selectedRoute! });
  const {
    run: reorderRouteStops,
    isLoading,
    success: stopSkipped,
    error,
  } = useUpdateRouteStopsOrder({ driverRoute: selectedRoute! });
  const { mapsApplication, updateMapsApplication } = useFetchMapsApplication();

  const sortedStopList = selectedRoute?.getSortedStopList({}) ?? [];
  const index = sortedStopList.findIndex((stop) => stop.id === stopId) ?? 0;
  const start = Math.max(index - AMOUNT_STOPS + 1, 0);
  const end = Math.min(index + AMOUNT_STOPS + 2, sortedStopList.length ?? 0);
  const shortenedStopList = sortedStopList.slice(start, end) ?? [];
  const currentStopOrder = stopId ? shortenedStopList.map((stop) => stop.id).indexOf(stopId) : undefined;
  const nextPendingStop = selectedRoute?.stops.find((stop) => stop.status === STOP_STATUS.pending);
  const onlyOneStopLeft =
    shortenedStopList.filter(
      (stop) =>
        stop.status === STOP_STATUS.pending && stop.id !== ROUTE_END_LOCATION && stop.id !== ROUTE_START_LOCATION
    ).length === 1;

  useEffect(() => {
    if (stopSkipped && selectedRoute) {
      setSkipModalStop(undefined);
      if (nextPendingStop)
        navigate(
          routing.PRIVATE_ROUTES.STOP_DETAILS.replace(`:routeId`, selectedRoute.id).replace(
            `:stopId`,
            nextPendingStop?.id
          )
        );
    }
    if (error) {
      setSkipModalStop(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopSkipped, error]);

  useEffect(() => {
    if (stopUpdated) {
      routeDispatch({
        type: RouteActionType.SET_EDITING_STOP,
        payload: undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopUpdated]);

  if (!selectedRoute || !vehicleData || !credentials) {
    return null;
  }

  // Function to handle the skip stop
  const handleSkipStop = (stopId: string) => {
    const stop = selectedRoute?.stops.find((s) => s.id === stopId);
    let deliveryId: string | undefined;
    if (stop?.type === `pickup`) {
      // If he is skipping means it has not been reported; so we shouldn't be worried about pickups completed and not deliveries
      const delivery = selectedRoute?.getDeliveryByPickupId(stopId);
      deliveryId = delivery?.id;
    }
    const stopIdsOrder =
      selectedRoute?.stops
        .filter((s) => s.id !== skipModalStop && s.status === `pending` && (!deliveryId || s.id !== deliveryId))
        .map((stop) => stop.id) ?? [];

    capture({
      type: EventTrackTypes.SkipStop,
      payload: {
        [`Action`]: `Skip`,
      },
    });

    reorderRouteStops({
      stops: [...stopIdsOrder, ...(skipModalStop ? [skipModalStop] : []), deliveryId].filter((s) => s !== undefined),
      optimize: false,
    });
  };

  // Functions to navigate to the next stop
  const onChangeFunctions = shortenedStopList.map((stop) => {
    return () => {
      if (!ROUTE_STOPS_BLACKLIST.includes(stop.id)) {
        navigate(routing.PRIVATE_ROUTES.STOP_DETAILS.replace(`:routeId`, selectedRoute.id).replace(`:stopId`, stop.id));
      }
    };
  });

  // Stop details
  const details = shortenedStopList.map((stop, index) => {
    if (
      (currentStopOrder !== undefined && Math.abs(index - currentStopOrder) > AMOUNT_STOPS - 1) ||
      ROUTE_STOPS_BLACKLIST.includes(stop.id)
    ) {
      return <RoutalLoader />;
    }
    return <StopDetails stop={stop} key={stop.id} />;
  });

  // Stop drags
  const drags = shortenedStopList.map((stop) => {
    if (!ROUTE_STOPS_BLACKLIST.includes(stop.id)) {
      let subLabelTime;
      if (credentials.test(`can_view_planning_times`)) {
        if (selectedRoute.status === ROUTE_STATUS.notStarted && stop.planned_arrival_time) {
          const date = parseSecondsToHM(stop.planned_arrival_time, true);
          subLabelTime = `${date}h`;
        }
        if (stop.estimated_arrival_time) {
          const date = dateFormatShortTime(stop.estimated_arrival_time);
          subLabelTime = intl.formatMessage({ id: `drivers.stop.pill.stop_estimated_arrival_at` }, { time: date });
        }
        if (stop.report && stop.report.created_at) {
          const date = dateFormatShortTime(stop.report.created_at);
          subLabelTime = intl.formatMessage({ id: `drivers.stop.pill.stop_reported_at` }, { time: date });
        }
      }

      return (
        <>
          <Grid style={{ padding: `0 20px ` }}>
            <RouteDragIcon />
            <StopPillInfo
              label={stop.location?.label}
              subLabel={stop.label}
              subLabelSelectable
              id={stop.id}
              stopOrder={stop.order + 1}
              maxStops={selectedRoute.stops.length}
              stopStatus={stop.status}
              stopReport={stop.report}
              isSpecificStop={stop.isSpecificStop}
              numberTasks={stop.tasks.length}
              subLabelTime={subLabelTime}
            />
          </Grid>

          {stop.status === STOP_STATUS.pending && (
            <>
              <Grid style={{ display: `flex`, flexDirection: `row` }}>
                <ActionButtons
                  mapsApplication={mapsApplication}
                  stop={stop}
                  onlyOneStopLeft={onlyOneStopLeft}
                  routeStatus={selectedRoute.status}
                  style={{ padding: `0px 20px` }}
                  onTriggerAction={(action) => {
                    if (action === `stop_counting`) {
                      setStopCountingModal(true);
                    }
                  }}
                  openSkipModal={() => {
                    capture({
                      type: EventTrackTypes.SkipStop,
                      payload: {
                        [`Action`]: `Open`,
                      },
                    });
                    setSkipModalStop(stop.id);
                  }}
                  openMapsModal={() => setShowMapsModal(true)}
                />
              </Grid>
              <VSpacer small />
            </>
          )}
        </>
      );
    } else {
      return null;
    }
  });

  // Stop footers
  const footers = shortenedStopList.map((stop) => {
    const hasPickupPending =
      !!stop.pickup_id && sortedStopList.find((s) => s.id === stop.pickup_id)?.status === STOP_STATUS.pending;

    const hasDeliveryCompleted =
      stop.type === `pickup` && sortedStopList.find((s) => s.pickup_id === stop.id)?.status !== STOP_STATUS.pending;

    const delivery = sortedStopList.find((s) => s.pickup_id === stop.id);

    if (stop.isSpecificStop) {
      if (stop.id === ROUTE_END_LOCATION && allStopsCompleted) {
        return (
          <RouteFooterContainer>
            <RouteFooter
              onClick={() => {
                endRoute && endRoute();
              }}
              intlId="driver.route.finish_route"
            />
          </RouteFooterContainer>
        );
      } else {
        return null;
      }
    } else {
      if (
        stop.status === STOP_STATUS.pending &&
        vehicleData?.project.fields.configuration.includes(`stop_duration`) &&
        startedStopId !== undefined &&
        routeState.startedReportStop !== stop.id
      ) {
        return null;
      }

      return (
        <StopDetailsFooter
          discardLoader={discardLoading}
          reportLoader={reportLoading}
          stop={stop}
          onComplete={(stopId: string) => {
            if (hasPickupPending) setStopPickupModal(stop);
            else onUpdate && onUpdate(stopId, `complete`);
          }}
          onCancel={(stopId: string) => {
            if (hasPickupPending) setStopPickupModal(stop);
            else onUpdate && onUpdate(stopId, `cancel`);
          }}
          onDiscard={(stopId: string) => {
            if (hasDeliveryCompleted) setStopDiscardWithDelivery(delivery);
            else onUpdate && onUpdate(stopId, `discard`);
          }}
          onNextStop={(stopId: string) => {
            if (hasPickupPending) setStopPickupModal(stop);
            else {
              capture({
                type: EventTrackTypes.ReorderRoute,
                payload: {
                  [`Action`]: `Open`,
                  [`From`]: `Mark as next`,
                },
              });
              navigate(
                `${routing.PRIVATE_ROUTES.ROUTE_REORDER.replace(`:routeId`, selectedRoute.id).concat(
                  `?stop=${stopId}&source=mark_as_next`
                )}`
              );
            }
          }}
          canUpdateRoute={stop.id !== nextPendingStop?.id && (canReorderStops ? true : false)}
          isCounting={startedStopId !== undefined}
          onStartStop={() => {
            if (hasPickupPending) setStopPickupModal(stop);
            else {
              capture({
                type: EventTrackTypes.StopTimer,
                payload: {
                  [`Action`]: `Start`,
                },
              });
              setReportDate(stopId!);
            }
          }}
        />
      );
    }
  });

  const stop: Stop | undefined = stopId ? sortedStopList.find((s) => s.id === stopId) : undefined;

  if (!stop) {
    navigate(routing.PRIVATE_ROUTES.ROUTE_DETAILS.replace(`:routeId`, selectedRoute.id));
  }

  return (
    <>
      <ModalSelect
        open={showMapsModal}
        title={{ intlId: `drivers.preferences.modal.map.title` }}
        subtitle={{ intlId: `drivers.preferences.modal.map.subtitle` }}
        selectors={[
          {
            selected: preferredMapModal === `google_maps`,
            onClick: () => {
              setPreferredMapModal(`google_maps`);
            },
            children: (
              <Text
                style={{ cursor: `pointer` }}
                intlId={`driver.stops.config.preferences.maps_application.google_maps`}
              />
            ),
          },
          {
            selected: preferredMapModal === `waze`,
            onClick: () => {
              setPreferredMapModal(`waze`);
            },
            children: (
              <Text style={{ cursor: `pointer` }} intlId={`driver.stops.config.preferences.maps_application.waze`} />
            ),
          },
          ...(capacitorService.isIOS() || capacitorService.isWeb()
            ? [
                {
                  selected: preferredMapModal === `apple_maps`,
                  onClick: () => {
                    setPreferredMapModal(`apple_maps`);
                  },
                  children: (
                    <Text
                      style={{ cursor: `pointer` }}
                      intlId={`driver.stops.config.preferences.maps_application.apple_maps`}
                    />
                  ),
                },
              ]
            : []),
        ]}
        buttons={[
          {
            intlId: `accept`,
            onClick: () => {
              if (preferredMapModal) {
                updateMapsApplication(preferredMapModal);
                if (stop?.location?.lat && stop?.location?.lng) {
                  openNavigationLink(stop?.location.lat, stop?.location.lng, preferredMapModal);
                }
              }

              setShowMapsModal(false);
            },
            style: {
              border: `none`,
            },
          },
          {
            intlId: `cancel`,
            variant: `text`,
            onClick: () => {
              setShowMapsModal(false);
            },
            style: {
              border: `none`,
            },
          },
        ]}
      />

      {selectedRoute.status !== `finished` && routeState.editingStop && (
        <EditStopSlidingMenu
          open
          isLoading={deletingStop || updatingStop}
          slideStyles={{ zIndex: 1007 }}
          editingStop={routeState.editingStop}
          onRemoveStop={(stop: Stop) => {
            deleteStop({ stop });
            routeDispatch({
              type: RouteActionType.SET_EDITING_STOP,
              payload: undefined,
            });
          }}
          onSaveChanges={(stop: Stop) => {
            updateStop({ stop, stopId: routeState.editingStop!.id });
          }}
          onExit={() => {
            routeDispatch({
              type: RouteActionType.SET_EDITING_STOP,
              payload: undefined,
            });
          }}
        />
      )}
      <Modal
        open={stopCountingModal}
        title={{
          intlId: `driver.stop.time.modal.title`,
        }}
        subtitle={{
          intlId: `driver.stop.time.modal.info`,
        }}
        buttons={[
          {
            intlId: `driver.stop.time.modal.reset`,
            onClick: () => {
              capture({
                type: EventTrackTypes.StopTimer,
                payload: {
                  [`Action`]: `Stop`,
                },
              });
              clearReportDate();
              setStopCountingModal(false);
            },
          },
          {
            intlId: `cancel`,
            onClick: () => {
              setStopCountingModal(false);
            },

            variant: `text`,
            style: {
              border: `none`,
            },
          },
        ]}
        children={<VSpacer medium />}
      />
      <Modal
        open={!!stopPickupModal && !!stopPickupModal?.pickup_id}
        title={{
          intlId: `driver.stop.delivery_without_pickup.modal.title`,
        }}
        subtitle={{
          intlId: `driver.stop.delivery_without_pickup.modal.info`,
        }}
        buttons={[
          {
            intlId: `driver.stop.delivery_without_pickup.modal.go`,
            onClick: () => {
              navigate(
                routing.PRIVATE_ROUTES.STOP_DETAILS.replace(`:routeId`, selectedRoute!.id).replace(
                  `:stopId`,
                  stopPickupModal!.pickup_id!
                )
              );
              setStopPickupModal(undefined);
            },
          },
          {
            intlId: `cancel`,
            onClick: () => {
              setStopPickupModal(undefined);
            },

            variant: `text`,
            style: {
              border: `none`,
            },
          },
        ]}
        children={<VSpacer medium />}
      />
      <Modal
        open={!!stopDiscardWithDelivery}
        isLoading={discardLoading}
        title={{
          intlId: `driver.stop.discard_pickup_with_delivery.modal.title`,
        }}
        subtitle={{
          intlId: `driver.stop.discard_pickup_with_delivery.modal.info`,
        }}
        buttons={[
          {
            intlId: `driver.stop.discard_pickup_with_delivery.modal.go`,
            onClick: () => {
              navigate(
                routing.PRIVATE_ROUTES.STOP_DETAILS.replace(`:routeId`, selectedRoute!.id).replace(
                  `:stopId`,
                  stopDiscardWithDelivery!.id
                )
              );
              setStopDiscardWithDelivery(undefined);
            },
          },
          {
            intlId: `cancel`,
            onClick: () => {
              setStopDiscardWithDelivery(undefined);
            },

            variant: `text`,
            style: {
              border: `none`,
            },
          },
        ]}
        children={<VSpacer medium />}
      />
      <Modal
        open={Boolean(skipModalStop) || isLoading}
        isLoading={isLoading}
        title={{
          intlId: `driver.stop.skip.modal.title`,
        }}
        subtitle={{
          intlId: `driver.stop.skip.modal.info`,
        }}
        buttons={[
          {
            intlId: `driver.stop.skip.modal.skip`,
            isLoading: isLoading,
            onClick: () => {
              handleSkipStop(skipModalStop!);
            },
          },
          {
            intlId: `cancel`,
            onClick: () => {
              setSkipModalStop(undefined);
            },
            isLoading: isLoading && !error,

            variant: `text`,
            style: {
              border: `none`,
            },
          },
        ]}
        children={<VSpacer medium />}
      />
      <DraggableContainer
        sizeOfFooter={70}
        sizeOfTopButtons={40}
        swipe={true}
        swipeIndex={shortenedStopList.findIndex((stop) => stop.id === stopId)}
        footer={selectedRoute.status === ROUTE_STATUS.finished ? false : footer}
        onChanges={onChangeFunctions}
        drags={drags}
        details={details}
        footers={footers}
      />
    </>
  );
};
